import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { Loader2, CheckCircle, XCircle, MapPin, MessageCircle, Calendar, Star, Heart, ExternalLink, History, BookmarkPlus } from 'lucide-react';
import DoctorAvatar from '@/components/DoctorAvatar';
import PlatformPulseCard from '@/components/public/PlatformPulseCard'; // patient-side rail (rebranded)

export default function DoctorConnectPage() {
  const { slug } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  // Structured name capture (P0 — Feb 2026). We collect first/middle/surname
  // separately so the doctor's Patient Log gets clean, sortable last-name data.
  // The composed `name` is what we POST to the backend for backwards-compat.
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [surname, setSurname] = useState('');
  const [mobile, setMobile] = useState('');
  const [locId, setLocId] = useState('');
  const todayStr = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(todayStr);
  const [q1, setQ1] = useState(null);
  const [q2, setQ2] = useState(null);
  const [q3, setQ3] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(null);  // { found, name, total_visits, visit_breakdown, push_available, otp_required, quota }
  const [lookupStatus, setLookupStatus] = useState('idle'); // 'idle' | 'pending' | 'done'
  const [otpToken, setOtpToken] = useState(null);
  const [otpKind, setOtpKind] = useState(null); // 'push' | 'sms' | null
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpErr, setOtpErr] = useState('');
  const [smsQuotaErr, setSmsQuotaErr] = useState('');
  const [forceContinue, setForceContinue] = useState(false);

  // Debounced lookup — when patient enters a 10+ digit mobile, fetch their
  // prior history and the doctor-defined identity-check requirement
  // (`otp_required` ∈ {push, sms, none}). Auto-trigger the matching OTP send.
  useEffect(() => {
    const digits = (mobile || '').replace(/\D/g, '');
    if (digits.length < 10) {
      setHistory(null); setOtpToken(null); setOtpKind(null); setOtpVerified(false);
      setLookupStatus('idle'); setForceContinue(false); setSmsQuotaErr('');
      return;
    }
    setLookupStatus('pending');
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/public/connect/${slug}/lookup`, { params: { mobile } });
        setHistory(data || null);
        const required = data?.otp_required || 'none';
        if (required === 'push' && !otpVerified) {
          try {
            setOtpSending(true); setOtpErr('');
            const r = await api.post(`/public/connect/${slug}/send-otp`, { mobile });
            setOtpToken(r.data.otp_token); setOtpKind('push');
          } catch { setOtpToken(null); setOtpKind(null); }
          finally { setOtpSending(false); }
        } else if (required === 'sms' && !otpVerified) {
          try {
            setOtpSending(true); setOtpErr(''); setSmsQuotaErr('');
            const r = await api.post(`/public/connect/${slug}/send-sms-otp`, { mobile });
            setOtpToken(r.data.otp_token); setOtpKind('sms');
          } catch (e) {
            setOtpToken(null); setOtpKind(null);
            // Quota / abuse / SMS provider failure — surface verbatim message
            // and let the patient continue without verification (degrade gracefully
            // so a doctor's quota cap never blocks a genuine new patient).
            setSmsQuotaErr(formatApiError(e.response?.data?.detail) || 'Could not send code right now.');
          } finally { setOtpSending(false); }
        } else {
          setOtpToken(null); setOtpKind(null);
        }
        if (data?.found && !name && data.name && required === 'none') {
          setName(data.name);
          // Hydrate structured triple from server hint (or split if older).
          const parts = (data.name || '').trim().split(/\s+/).filter(Boolean);
          if (parts.length === 1) setFirstName(parts[0]);
          else if (parts.length === 2) { setFirstName(parts[0]); setSurname(parts[1]); }
          else if (parts.length >= 3) { setFirstName(parts[0]); setMiddleName(parts.slice(1, -1).join(' ')); setSurname(parts[parts.length - 1]); }
        }
      } catch { setHistory(null); }
      finally { setLookupStatus('done'); }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobile, slug]);

  // Reveal the rest of the form only when:
  //  • Lookup resolved and `otp_required === 'none'` (returning known patient
  //    without app, OR new-but-seen-elsewhere-in-DocSite — both trusted), OR
  //  • Patient verified the push or SMS OTP, OR
  //  • Patient clicked "Continue without verification" (graceful fallback,
  //    e.g. when SMS provider failed or doctor's free-tier quota is exhausted).
  const revealForm =
    forceContinue ||
    otpVerified ||
    (lookupStatus === 'done' && (history?.otp_required === 'none' || !history?.otp_required));

  const verifyOtp = async () => {
    if (!otpToken || otpCode.length < 4) return;
    setOtpErr('');
    try {
      const { data } = await api.post(`/public/connect/${slug}/verify-otp`, { otp_token: otpToken, code: otpCode });
      if (data?.ok) {
        setOtpVerified(true);
        if (data.name) {
          setName(data.name);
          const parts = (data.name || '').trim().split(/\s+/).filter(Boolean);
          if (parts.length === 1) setFirstName(parts[0]);
          else if (parts.length === 2) { setFirstName(parts[0]); setSurname(parts[1]); }
          else if (parts.length >= 3) { setFirstName(parts[0]); setMiddleName(parts.slice(1, -1).join(' ')); setSurname(parts[parts.length - 1]); }
        }
      }
    } catch (e) {
      setOtpErr(formatApiError(e.response?.data?.detail) || 'Wrong code');
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/public/connect/${slug}`);
        if (!alive) return;
        setInfo(data);
        if (data.locations?.length === 1) setLocId(data.locations[0].id);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || 'Doctor not found');
      } finally { setLoading(false); }
    })();
    return () => { alive = false; };
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimFirst = (firstName || '').trim();
    const trimMid = (middleName || '').trim();
    const trimSur = (surname || '').trim();
    if (!trimFirst || trimFirst.length < 2) { setErr('First name is required (min 2 letters).'); return; }
    if (!mobile.trim()) return;
    const composedFull = [trimFirst, trimMid, trimSur].filter(Boolean).join(' ');
    setSubmitting(true); setErr('');
    // Mirror the composed full name into the legacy `name` state so the
    // thank-you screen (which addresses the patient by name) renders cleanly
    // even for brand-new visitors who never triggered the lookup auto-fill.
    setName(composedFull);
    try {
      const { data } = await api.post(`/public/connect/${slug}`, {
        name: composedFull,
        first_name: trimFirst,
        middle_name: trimMid,
        surname: trimSur,
        mobile: mobile.trim(),
        location_id: locId || null,
        appointment_date: date || null,
        q1, q2, q3,
      });
      setResult(data);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || 'Could not submit');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="animate-spin text-sky-600" size={28} />
    </div>
  );
  if (err && !info) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md text-center">
        <p className="text-base font-medium text-slate-900">{err}</p>
      </div>
    </div>
  );

  const d = info?.doctor;
  const locs = info?.locations || [];

  if (result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-sky-50 to-violet-50 p-4 md:p-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <div className="max-w-md mx-auto bg-white rounded-3xl border border-slate-200 p-6 shadow-xl" data-testid="connect-thankyou">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <Heart size={26} className="text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {result.duplicate ? 'Welcome back' : 'You\'re connected'}
            </h1>
            <p className="text-sm text-slate-600 mt-1">{name}, you're now in {result.doctor_name}'s patient directory.</p>
          </div>

          <div className="mt-5 space-y-2.5">
            {result.tqs_url && (
              <a href={result.tqs_url} className="flex items-center gap-3 p-3.5 rounded-2xl bg-sky-50 border border-sky-200 hover:bg-sky-100" data-testid="thankyou-tqs">
                <Star size={18} className="text-amber-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">Share quick feedback (4 questions)</p>
                  <p className="text-[11px] text-slate-500">Helps {result.doctor_name} improve and rank higher on Google.</p>
                </div>
                <ExternalLink size={14} className="text-sky-600 shrink-0" />
              </a>
            )}
            {result.whatsapp_url && (
              <a href={result.whatsapp_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3.5 rounded-2xl bg-emerald-50 border border-emerald-200 hover:bg-emerald-100" data-testid="thankyou-whatsapp">
                <MessageCircle size={18} className="text-emerald-600" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">WhatsApp the doctor</p>
                  <p className="text-[11px] text-slate-500">Pre-filled with your name. Tap to send.</p>
                </div>
                <ExternalLink size={14} className="text-emerald-600 shrink-0" />
              </a>
            )}
            <a href={result.book_url} className="flex items-center gap-3 p-3.5 rounded-2xl bg-violet-50 border border-violet-200 hover:bg-violet-100" data-testid="thankyou-book">
              <Calendar size={18} className="text-violet-600" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900">Book your next visit</p>
                <p className="text-[11px] text-slate-500">See available slots & book in 1 tap.</p>
              </div>
              <ExternalLink size={14} className="text-violet-600 shrink-0" />
            </a>
            <a href={`/register?connect_to=${result.doctor_slug}&phone=${encodeURIComponent(mobile)}&from=connect_thankyou`} className="flex items-center gap-3 p-3.5 rounded-2xl bg-amber-50 border border-amber-200 hover:bg-amber-100" data-testid="thankyou-save-clinic">
              <BookmarkPlus size={18} className="text-amber-600" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900">Save Dr. {result.doctor_name?.replace(/^Dr\.?\s+/, '').split(' ')[0]} to your phone</p>
                <p className="text-[11px] text-slate-500">Create a free patient account · 1-tap rebooking · history saved forever.</p>
              </div>
              <ExternalLink size={14} className="text-amber-600 shrink-0" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen py-6 px-4 relative overflow-hidden"
      style={{
        fontFamily: 'Manrope, sans-serif',
        backgroundColor: '#f8fafc',
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 280 280' width='280' height='280'><g fill='none' stroke='%230ea5e9' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' opacity='0.55'><g transform='translate(28,28)'><path d='M8 2 v10 a6 6 0 0 0 12 0 v-10' /><path d='M14 18 v8 a8 8 0 0 0 16 0 v-6' /><circle cx='30' cy='14' r='3' /></g><g transform='translate(186,52)'><path d='M0 16 h12 l4 -10 l6 20 l4 -10 h16' /></g><g transform='translate(50,186)'><rect x='6' y='0' width='12' height='28' rx='2' /><rect x='-2' y='8' width='28' height='12' rx='2' /></g><g transform='translate(186,200) rotate(-30 18 9)'><rect x='0' y='0' width='40' height='16' rx='8' /><line x1='20' y1='0' x2='20' y2='16' /></g></g></svg>\")",
        backgroundRepeat: 'repeat',
        backgroundSize: '260px 260px',
      }}
    >
      {/* On lg+ screens we split the page into [center card, max-md] + [right rail, 320-360px].
          On mobile/tablet the rail hides and the centered card fills the viewport naturally. */}
      <div className="relative mx-auto flex flex-col lg:flex-row lg:items-start lg:justify-center gap-6 lg:gap-10 max-w-[900px] xl:max-w-[1100px]">
        <div className="w-full max-w-md mx-auto lg:mx-0">
        {/* DocSite branded header */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <a href="/" className="flex items-center gap-2" data-testid="connect-docsite-logo">
            <img src="/docsite-logo.png" alt="DocSite" className="h-9 w-9 object-contain" />
            <span className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>DocSite</span>
            <span className="text-[10px] text-slate-400 hidden sm:inline">· Verified Doctor Profile</span>
          </a>
        </div>

        {/* Doctor identity card */}
        <div className="bg-white rounded-3xl border border-slate-200 p-5 mb-4 shadow-sm flex items-center gap-4" data-testid="connect-doctor-card">
          <DoctorAvatar photoUrl={d?.photo_url} gender={d?.gender} name={d?.name} size={64} />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold text-sky-600 uppercase tracking-widest">DocSite · Connect · Feedback · History</p>
            <h1 className="text-lg font-bold text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{d?.name}</h1>
            <p className="text-xs text-slate-500 truncate">{d?.qualifications}</p>
            {d?.experience_years && <p className="text-[11px] text-slate-400">{d.experience_years} years experience</p>}
            {info?.live_location && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200" data-testid="live-location-chip">
                <span className="relative inline-flex items-center justify-center">
                  <span className="absolute h-2 w-2 rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                <span className="text-[11px] font-semibold text-emerald-800 truncate max-w-[180px]">Live now · {info.live_location.name}</span>
                {info.live_location.until && <span className="text-[10px] text-emerald-700/70 hidden sm:inline">till {info.live_location.until}</span>}
              </div>
            )}
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-3xl border border-slate-200 p-5 md:p-6 shadow-sm" data-testid="connect-form">
          <p className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Connect with {d?.name}</p>
          <p className="text-xs text-slate-500 mt-1">Quick 30-second introduction. No account, no password — just so the doctor can stay in touch and you can book again easily.</p>

          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            {/* Mobile FIRST — drives lookup, OTP, and prefill */}
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1 block">Patient Mobile</label>
              <input value={mobile} onChange={e => setMobile(e.target.value.replace(/[^0-9+ ]/g, ''))} required
                placeholder="e.g. 98765 43210" inputMode="tel"
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-300"
                data-testid="connect-mobile" />
              <p className="text-[10px] text-slate-400 mt-1">Returning patient? We'll auto-fill the rest after lookup.</p>
            </div>

            {/* Welcome-back banner — only shown when this mobile has prior
                visits with this doctor. Shows per-clinic count breakdown. */}
            {history?.found && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="connect-welcome-back">
                <div className="flex items-center gap-2 mb-1.5">
                  <History size={14} className="text-emerald-700" />
                  <p className="text-sm font-semibold text-emerald-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Welcome back, {history.name?.split(' ')[0] || 'there'}
                  </p>
                </div>
                <p className="text-[11px] text-emerald-800 mb-2">You've seen {d?.name} <b>{history.total_visits}</b> time{history.total_visits === 1 ? '' : 's'} before.</p>
                <div className="flex flex-wrap gap-1.5">
                  {history.visit_breakdown.map((b, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-emerald-200 text-[11px] font-medium text-emerald-800" data-testid={`connect-history-${i}`}>
                      <MapPin size={10} /> {b.location_name} <b className="text-emerald-900">×{b.count}</b>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* OTP block — renders for either DocSite app push (returning patient
                with the app) or SMS (genuinely new patient with no DocSite footprint). */}
            {history?.otp_required && history.otp_required !== 'none' && !otpVerified && otpToken && (
              <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4" data-testid="connect-otp-block">
                <p className="text-sm font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Verify it's you</p>
                <p className="text-[11px] text-slate-600 mb-3">
                  {otpSending && otpKind === 'push' && 'Sending code to your DocSite app…'}
                  {otpSending && otpKind === 'sms' && `Sending a code via SMS to ${mobile}…`}
                  {!otpSending && otpKind === 'push' && 'Open the DocSite app on your phone — we sent a 4-digit code there. Enter it below.'}
                  {!otpSending && otpKind === 'sms' && `We sent a verification code to ${mobile} via SMS. Enter it below.`}
                </p>
                <div className="flex gap-2">
                  <input value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputMode="numeric" placeholder="••••" maxLength={6}
                    className="w-32 rounded-xl border border-sky-300 px-3 py-2 text-center text-lg font-mono tracking-[0.4em] outline-none focus:ring-2 focus:ring-sky-300"
                    data-testid="connect-otp-input" />
                  <button type="button" onClick={verifyOtp} disabled={otpCode.length < 4}
                    className="rounded-xl px-4 py-2 bg-sky-600 text-white text-xs font-semibold hover:bg-sky-700 disabled:opacity-40"
                    data-testid="connect-otp-verify">Verify</button>
                </div>
                {otpErr && <p className="text-[11px] text-rose-600 mt-2">{otpErr}</p>}

                {/* Free-plan SMS quota disclosure (per founder spec). Shown only
                    on the SMS branch so app-OTP returning patients aren't confused. */}
                {otpKind === 'sms' && history?.quota?.plan === 'free' && (
                  <p className="text-[10px] text-slate-500 mt-2 leading-snug" data-testid="connect-sms-quota-note">
                    Only 2 OTPs per day under Free Plan, max 10/month.
                    <span className="hidden sm:inline"> If you have higher transactions, please subscribe to a minimum paid plan.</span>
                    {typeof history.quota.used_today === 'number' && (
                      <span className="block text-[10px] text-slate-400 mt-0.5">
                        {history.quota.used_today}/{history.quota.daily_limit} used today · {history.quota.used_month}/{history.quota.monthly_limit} this month
                      </span>
                    )}
                  </p>
                )}
              </div>
            )}

            {/* SMS quota / send error — graceful degrade so the patient is never trapped. */}
            {smsQuotaErr && !otpToken && !otpVerified && history?.otp_required === 'sms' && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3" data-testid="connect-sms-quota-blocked">
                <p className="text-[11px] text-amber-900 leading-relaxed">{smsQuotaErr}</p>
                <button type="button" onClick={() => setForceContinue(true)}
                  className="mt-2 text-[11px] text-amber-700 hover:text-amber-900 underline underline-offset-2 font-semibold"
                  data-testid="connect-sms-quota-skip">
                  Continue without verification
                </button>
              </div>
            )}

            {otpVerified && (
              <p className="text-[11px] text-emerald-700 font-medium flex items-center gap-1.5" data-testid="connect-otp-verified"><CheckCircle size={12} /> Identity verified — your data is auto-filled below.</p>
            )}

            {/* Lookup pending hint — shows while the backend resolves the
                10-digit number. Keeps the patient anchored on the mobile field. */}
            {lookupStatus === 'pending' && (
              <p className="text-[11px] text-slate-500 flex items-center gap-1.5" data-testid="connect-lookup-pending">
                <Loader2 size={12} className="animate-spin" /> Checking your number…
              </p>
            )}

            {/* Soft fallback — when an OTP step is required but the user can't
                receive it (no app + delayed SMS), allow them to proceed. */}
            {history?.otp_required && history.otp_required !== 'none' && !otpVerified && !forceContinue && otpToken && (
              <button type="button" onClick={() => setForceContinue(true)}
                className="text-[11px] text-sky-600 hover:text-sky-700 underline underline-offset-2"
                data-testid="connect-skip-otp">
                {otpKind === 'push' ? "Don't have the DocSite app? Continue without verification" : "Didn't receive the SMS? Continue without verification"}
              </button>
            )}

            {revealForm && (<>
            {/* Structured name fields — First (required), Middle (optional), Surname
                (please share) so the Patient Log shows the full proper name. */}
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1 block">
                First name <span className="text-rose-600">*</span>
              </label>
              <input value={firstName} onChange={e => setFirstName(e.target.value)} required minLength={2} maxLength={40}
                placeholder="e.g. Anjali"
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-300"
                data-testid="connect-first-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Middle name <span className="text-slate-400 font-normal">(optional)</span></label>
                <input value={middleName} onChange={e => setMiddleName(e.target.value)} maxLength={40}
                  placeholder="e.g. Devi"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-300"
                  data-testid="connect-middle-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Surname <span className="text-amber-600 font-medium">(please share)</span></label>
                <input value={surname} onChange={e => setSurname(e.target.value)} maxLength={40}
                  placeholder="e.g. Sharma"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-300"
                  data-testid="connect-surname" />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 -mt-1 leading-snug">
              Your doctor would love to address you by your full name on file.
            </p>
            {locs.length > 0 && (
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Where did the doctor see you?</label>
                <div className="space-y-2">
                  {locs.map(l => (
                    <label key={l.id} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${locId === l.id ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`connect-loc-${l.id}`}>
                      <input type="radio" name="loc" value={l.id} checked={locId === l.id} onChange={() => setLocId(l.id)} className="mt-1 accent-sky-600" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900">{l.name}</p>
                        <p className="text-[11px] text-slate-500 flex items-center gap-1"><MapPin size={10} /> {l.city}{l.address ? ` · ${l.address}` : ''}</p>
                      </div>
                    </label>
                  ))}
                  <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${locId === '' ? 'border-slate-400 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <input type="radio" name="loc" value="" checked={locId === ''} onChange={() => setLocId('')} className="accent-slate-600" />
                    <span className="text-sm text-slate-600">Not sure / Other</span>
                  </label>
                </div>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1 block">Visit Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-300"
                data-testid="connect-date" />
            </div>

            {/* Quick visit feedback — 3 questions only. Q4 (treatment-worked)
                is asked separately via the 7-day TQS follow-up. */}
            <div className="rounded-2xl border border-sky-200 bg-sky-50/40 p-4">
              <p className="text-xs font-bold text-sky-800 uppercase tracking-wider">Quick visit feedback</p>
              <p className="text-[11px] text-slate-600 mt-0.5 mb-3">3 honest yes/no questions about the consultation. Helps the doctor improve.</p>
              {[
                { key: 'q1', q: q1, set: setQ1, title: 'Did the doctor hear your symptoms fully?' },
                { key: 'q2', q: q2, set: setQ2, title: 'Did the doctor examine, explain, and address your concerns?' },
                { key: 'q3', q: q3, set: setQ3, title: 'Did the doctor share clear next steps and likely outcome?' },
              ].map(({ key, q, set, title }, i) => (
                <div key={key} className="mb-3 last:mb-0">
                  <p className="text-xs font-medium text-slate-800 mb-1.5">{i + 1}. {title}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => set(true)}
                      className={`py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 ${q === true ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                      data-testid={`connect-${key}-yes`}>
                      <CheckCircle size={12} /> Yes
                    </button>
                    <button type="button" onClick={() => set(false)}
                      className={`py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 ${q === false ? 'bg-rose-600 text-white' : 'bg-rose-50 text-rose-700 hover:bg-rose-100'}`}
                      data-testid={`connect-${key}-no`}>
                      <XCircle size={12} /> No
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {err && <p className="text-xs text-rose-600">{err}</p>}
            <button type="submit" disabled={submitting || !(firstName || '').trim() || !mobile.trim()}
              className="w-full py-3.5 rounded-full bg-sky-600 text-white text-sm font-bold hover:bg-sky-700 disabled:opacity-40 flex items-center justify-center gap-2"
              data-testid="connect-submit">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
              Connect with {d?.name?.split(' ').slice(0, 2).join(' ')}
            </button>
            <p className="text-center text-[10px] text-slate-400">Your details are private with the doctor.</p>
            </>)}
          </form>
        </div>
        </div>

        {/* Desktop right-rail: Platform pulse + What's New teaser + trust line.
            Hidden on mobile & tablet to preserve the form-first conversion flow. */}
        <PlatformPulseCard />
      </div>
    </div>
  );
}
